import React, { useState } from 'react';
import './AddClientUser.css';
import { api } from '../../utils/authApi';

const AddClientUser = () => {
  const [formData, setFormData] = useState({
    projectName: '',
    projectLocation: '',
    clientName: '',
    clientEmail: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  const generatePassword = () => {
    // Generate a random password (12 characters with mix of upper, lower, numbers, symbols)
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.projectName || !formData.projectLocation || !formData.clientName || !formData.clientEmail) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    // Auto-generate password
    const generatedPassword = generatePassword();

    try {
      // Step 1: Create the user (JWT token automatically included by api.post)
      const userResponse = await api.post('/users/add', {
        name: formData.clientName,
        email: formData.clientEmail,
        password: generatedPassword,
        role: 'client'
      });

      if (!userResponse.ok) {
        throw new Error('Failed to create client user');
      }

      const userData = await userResponse.json();
      console.log('User created:', userData);

      // Step 2: Get the client ID using the email
      const clientIdResponse = await api.get(`/users/clientID/${formData.clientEmail}`);
      
      if (!clientIdResponse.ok) {
        throw new Error('Failed to retrieve client ID');
      }

      const clientIdData = await clientIdResponse.json();
      console.log('Client ID data:', clientIdData);

      // Extract the user ID from the response (it's in an array format)
      const userId = clientIdData.Message[0][0];
      console.log('User ID:', userId);

      // Step 3: Create the project with the retrieved user_id
      const projectResponse = await api.post('/projects/add', {
        user_id: userId,
        location: formData.projectLocation,
        email: formData.clientEmail,
        name: formData.projectName
      });

      if (!projectResponse.ok) {
        throw new Error('Failed to create project');
      }

      const projectData = await projectResponse.json();
      console.log('Project created:', projectData);
      
      setSuccessMessage(`Client user and project created successfully! Login credentials have been sent to ${formData.clientEmail}`);
      
      // Reset form
      setFormData({
        projectName: '',
        projectLocation: '',
        clientName: '',
        clientEmail: '',
      });
      
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(error.message || 'An error occurred while creating the client user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      projectName: '',
      projectLocation: '',
      clientName: '',
      clientEmail: '',
    });
    setSuccessMessage('');
    setErrorMessage('');
  };

  return (
    <div className="add-client-container">
      <div className="add-client-card">
        <h2 className="add-client-title">Add New Client User</h2>
        
        <form onSubmit={handleSubmit} className="add-client-form">
          {/* Project Name */}
          <div className="form-group">
            <label htmlFor="projectName" className="form-label">
              Project Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="projectName"
              name="projectName"
              value={formData.projectName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter project name"
              required
            />
          </div>

          {/* Project Location */}
          <div className="form-group">
            <label htmlFor="projectLocation" className="form-label">
              Project Location <span className="required">*</span>
            </label>
            <input
              type="text"
              id="projectLocation"
              name="projectLocation"
              value={formData.projectLocation}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter project location (e.g., Building A, Floor 3)"
              required
            />
          </div>

          {/* Client Name */}
          <div className="form-group">
            <label htmlFor="clientName" className="form-label">
              Client Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="clientName"
              name="clientName"
              value={formData.clientName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter client's full name"
              required
            />
          </div>

          {/* Client Email */}
          <div className="form-group">
            <label htmlFor="clientEmail" className="form-label">
              Client Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="clientEmail"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleInputChange}
              className="form-input"
              placeholder="client@example.com"
              required
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="message message-error">
              {errorMessage}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="message message-success">
              {successMessage}
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary btn-submit"
            >
              {isLoading ? 'Creating User...' : 'Create Client User'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="btn btn-reset"
            >
              Reset
            </button>
          </div>

          {/* Password Info Message */}
          <div className="password-info">
            <p>
              <strong>Note:</strong> A secure password will be automatically generated and sent to the client's email address.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientUser;